/**
 * Signals — show view on the shared Inspector grammar (design-system §5).
 *
 * Header carries the Signal named primary actions (triage · convert to evidence
 * · promote to package — {@link SignalActions}). Relations link the host
 * Context, the source entity, the candidate Package, and any promoted Evidence
 * (coverage §2). Body renders the signal text.
 */
import { Icon, Layers, Package, Radio } from "@/components/ui/icon";
import type { Signal } from "@aos/shared";

import { useGo, useNavigation } from "@refinedev/core";
import { Button } from "@/components/ui/button";
import {
  Field,
  RefLink,
  ShowPage,
  TechnicalId,
  type RelationGroup,
} from "@/components/inspector";
import { humanize } from "@/lib/format";
import { Markdown } from "@/components/app/MarkdownView";
import { formatDateTime } from "../_shared/runs-ui";

import { SignalActions } from "./SignalActions";

/** Map a signal `source_type` to the Refine resource its `source_id` links to. */
const SOURCE_TYPE_RESOURCE: Record<string, string> = {
  artifact: "artifacts",
  event: "events",
  document: "documents",
  metric: "metrics",
  measurement: "measurements",
  agent_run: "agent-runs",
  rule_run: "rule-runs",
  package: "packages",
};

function scoreLabel(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return value.toFixed(2);
}

export function SignalShow() {
  const go = useGo();
  const { editUrl } = useNavigation();

  return (
    <ShowPage<Signal>
      resource="signals"
      title={(s) => s.title}
      subtitle="A pain, anomaly, opportunity, or risk awaiting triage."
      status={(s) => s.status}
      primaryAction={(s) => <SignalActions signal={s} />}
      actions={(s) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go({ to: editUrl("signals", s.id) })}
        >
          <Icon name="edit" size={16} className="mr-2" aria-hidden /> Edit
        </Button>
      )}
      overview={(s) => (
        <>
          <Field label="Signal type">{humanize(s.signal_type)}</Field>
          <Field label="Strength">{scoreLabel(s.strength)}</Field>
          <Field label="Confidence">{scoreLabel(s.confidence_score)}</Field>
          <Field label="Source type">
            {s.source_type ? humanize(s.source_type) : null}
          </Field>
          <Field label="Candidate package type">
            {s.candidate_package_type
              ? humanize(s.candidate_package_type)
              : null}
          </Field>
          <Field label="Created by">
            <RefLink resource="users" id={s.created_by_user_id} />
          </Field>
        </>
      )}
      technical={(s) => (
        <>
          <Field label="Id">
            <TechnicalId id={s.id} />
          </Field>
          <Field label="Created">{formatDateTime(s.created_at)}</Field>
          <Field label="Updated">{formatDateTime(s.updated_at)}</Field>
        </>
      )}
      body={(s) => (s.body ? <Markdown source={s.body} /> : null)}
      relations={(s): RelationGroup[] => {
        const sourceResource = s.source_type
          ? SOURCE_TYPE_RESOURCE[s.source_type]
          : undefined;
        return [
          {
            title: "References",
            items: [
              {
                label: "Host Context",
                icon: Layers,
                resource: "contexts",
                id: s.context_id,
              },
              {
                label: s.source_type
                  ? `Source ${humanize(s.source_type)}`
                  : "Source",
                icon: Radio,
                resource: sourceResource,
                id: sourceResource ? s.source_id : undefined,
                node:
                  !sourceResource && s.source_id ? (
                    <span className="font-mono text-xs">{s.source_id}</span>
                  ) : undefined,
              },
              {
                label: "Candidate Package",
                icon: Package,
                resource: "packages",
                id: s.candidate_package_id,
              },
            ],
          },
          {
            title: "Promoted Evidence",
            emptyMessage:
              "No evidence promoted from this signal yet (created via convert-to-evidence).",
            items: [],
          },
        ];
      }}
    />
  );
}

export default SignalShow;
